/**
 * @file: desktop-layout/index.tsx
 *
 * Desktop (>=768px web) master–detail layout for the inbox — a 2/3-column
 * presentation of the same surfaces the mobile stack pushes:
 *
 *   ┌──────────┬───────────────────────┬──────────────┐
 *   │ master   │ detail                │ info (col 3) │
 *   │ (list)   │ feed thread / chat    │ group info   │
 *   │          │ room / placeholder    │ (optional)   │
 *   └──────────┴───────────────────────┴──────────────┘
 *
 * Owns the selection/info state directly off the inbox context: the active
 * thread (a feed thread or a resolved chat conversation) and whether the chat
 * room-info column is open. The transient overlay is owned by
 * `InboxOverlayHost`. Mounted by the inbox `_index` screen on desktop.
 */

import {
  BakedConversationHandle,
  isBaked,
} from '@euler/app/flows/chat/lib/conversation/handle';
import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { ChatContactPickerParams } from '@euler/app/flows/chat/lib/types';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { FC, memo, useCallback, useEffect } from 'react';
import { View } from 'react-native';
import { useInboxContext } from '../../context';
import { DetailColumn } from './detail-column';
import { InfoColumn } from './info-column';
import { MasterColumn } from './master-column';

export const InboxDesktopLayout: FC = memo(() => {
  const { activeThread$, overlay$, infoOpen$, nonce$ } = useInboxContext();
  const [activeThread] = useBehaviorSubject(activeThread$);
  const [infoOpen] = useBehaviorSubject(infoOpen$);
  const [nonce] = useBehaviorSubject(nonce$);

  const activeConversation =
    activeThread?.type === 'conversation' ? activeThread.handle : null;

  // Auto-close the info column if the selected room becomes closed for me
  // (removed / dismissed) while it's open — the room detail would 404. The
  // conversation body publishes closure to the session.
  const { closedRoomIds$ } = useChatSession();
  const [closedRoomIds] = useBehaviorSubject(closedRoomIds$);
  const activeRoomId =
    activeConversation && isBaked(activeConversation)
      ? activeConversation.roomId
      : null;
  useEffect(() => {
    if (
      activeRoomId &&
      closedRoomIds.has(activeRoomId) &&
      infoOpen$.getValue()
    ) {
      infoOpen$.next(false);
    }
  }, [activeRoomId, closedRoomIds, infoOpen$]);

  const onOpenRoomInfo = useCallback(() => infoOpen$.next(true), [infoOpen$]);
  const onCloseInfo = useCallback(() => infoOpen$.next(false), [infoOpen$]);
  // Leaving/dismissing/deleting removes the conversation — deselect it (so the
  // detail column drops to the placeholder) and close the info pane.
  const onExitConversation = useCallback(() => {
    activeThread$.next(null);
    infoOpen$.next(false);
  }, [activeThread$, infoOpen$]);

  // First send to a contact with no room yet materialises the room: swap the
  // selection's handle bare→baked (keeping `selectionKey`, so the pane updates
  // in place, no remount) so the new thread highlights + room info is real.
  const onConversationHandleMaterialized = useCallback(
    (baked: BakedConversationHandle) => {
      const current = activeThread$.getValue();
      if (current?.type === 'conversation') {
        activeThread$.next({ ...current, handle: baked });
      }
    },
    [activeThread$],
  );
  const onAddMembers = useCallback(
    (params: ChatContactPickerParams) =>
      overlay$.next({ kind: 'picker', params }),
    [overlay$],
  );

  return (
    <View style={{ flex: 1, flexDirection: 'row', backgroundColor: '#fff' }}>
      <MasterColumn />
      <DetailColumn
        activeThread={activeThread}
        onOpenRoomInfo={onOpenRoomInfo}
        onConversationHandleMaterialized={onConversationHandleMaterialized}
        onExitConversation={onExitConversation}
      />
      {infoOpen && activeConversation ? (
        <InfoColumn
          handle={activeConversation}
          nonce={nonce}
          onAddMembers={onAddMembers}
          onClose={onCloseInfo}
          onExit={onExitConversation}
        />
      ) : null}
    </View>
  );
});
